import axios from 'axios';

  // Get API URL from environment variable or use relative path
// In production, this will use the nginx proxy at /api
// In development, VITE_API_URL should be set to http://localhost:5174/api
const API_URL = import.meta.env.VITE_API_URL || '/api';

// Export the API URL for use in non-axios contexts (e.g., PDF loading with fetch)
export const getApiBaseUrl = () => API_URL;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // On 401 (session expired / not authenticated), notify AuthContext via a
    // global event. AuthContext clears the user state, which makes
    // ProtectedRoute redirect to /login immediately (no manual refresh needed).
    // Login/2FA/logout endpoints are excluded: a wrong-password 401 or a 401 on
    // sign-out is expected and must not surface as "session expired".
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // /auth/me 401 is the normal anonymous probe (and the idle-session poll).
      // AuthContext handles both; do not treat the probe as a sudden expiry event.
      const isAuthAttempt = url.includes('/auth/login')
        || url.includes('/auth/verify')
        || url.includes('/auth/logout')
        || url.includes('/auth/me');
      if (!isAuthAttempt) {
        window.dispatchEvent(new CustomEvent('pfp:unauthorized'));
      }
    }

    // A 503 from the maintenance guard is not an application error - send the
    // user to the maintenance screen instead of showing a red error toast.
    if (error.response?.status === 503 && error.response?.data?.maintenance) {
      redirectToMaintenance();
    }

    return Promise.reject(error);
  }
);

/**
 * Send the browser to the maintenance screen. Uses a hard navigation because
 * this runs outside the React router, and guards against looping when the
 * maintenance page itself makes a call.
 */
function redirectToMaintenance() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/maintenance') return;
  window.location.assign('/maintenance');
}

/**
 * fetch wrapper for the few pages that do not use axios. It applies the same
 * maintenance handling so they cannot surface a raw 503 as a red toast.
 */
export async function apiFetch(input, init = {}) {
  const response = await fetch(input, { credentials: 'include', ...init });

  if (response.status === 503) {
    let isMaintenance = false;
    try {
      // Clone so the caller can still read the body.
      const body = await response.clone().json();
      isMaintenance = Boolean(body?.maintenance);
    } catch {
      // Non-JSON 503 (e.g. a proxy page) - treat as maintenance too.
      isMaintenance = true;
    }
    if (isMaintenance) redirectToMaintenance();
  }

  return response;
}

export default api;

